import {
  ChangeDetectorRef,
  DestroyRef,
  inject,
  Pipe,
  PipeTransform,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ObfuscationService, ObfuscationType } from './obfuscation.service';

/**
 * Pipe to obfuscate PII values in templates when Marketing Mode is active.
 *
 * Usage: {{ value | prObfuscate:'name' }}
 * Types: 'name' | 'firstName' | 'lastName' | 'email' | 'phone' | 'id' | 'dob' | 'address'
 *
 * Impure because output depends on global toggle state, not just input value.
 * Subscribes to isActive$ and calls markForCheck() so that OnPush host
 * components re-render when the toggle changes in a sibling component tree.
 */
@Pipe({ name: 'prObfuscate', pure: false, standalone: true })
export class ObfuscatePipe implements PipeTransform {
  private _obfuscation = inject(ObfuscationService);
  private _cdr = inject(ChangeDetectorRef);
  private _destroyRef = inject(DestroyRef);
  private _lastActive = false;
  private _lastValue = '';
  private _lastType: ObfuscationType = 'name';
  private _lastResult = '';

  constructor() {
    this._obfuscation.isActive$
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(() => this._cdr.markForCheck());
  }

  transform(value: string, type: ObfuscationType = 'name'): string {
    if (!value) {
      return value;
    }

    const isActive = this._obfuscation.isActiveSnapshot;

    if (
      value === this._lastValue &&
      type === this._lastType &&
      isActive === this._lastActive
    ) {
      return this._lastResult;
    }

    this._lastValue = value;
    this._lastType = type;
    this._lastActive = isActive;
    this._lastResult = this._obfuscation.transform(value, type);

    return this._lastResult;
  }
}
